'use client';

import { AlertTriangle, Clock } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from '@/hooks/useSubscription';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { navigateApp } from '@/lib/navigation';

// UTC: these instants are Eastern midnight (stored UTC), so the calendar day lives in UTC. Formatting
// in the viewer's local zone shifts west-of-UTC users a day early — and would disagree with Settings,
// which already formats in UTC.
function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Thin status strip for subscriptions that need attention: read-only, canceled, or on trial.
 * Renders nothing for a healthy paid subscription. Non-admins get only the read-only notice — the
 * billing detail and the recovery link are admin business, and Settings is admin-only anyway.
 */
export function SubscriptionBanner() {
    const { subscription, isReadOnly } = useSubscription();
    const { isAdmin } = useCurrentUser();

    if (!subscription) return null;

    // Non-admins see one thing only: that the account is read-only, with no billing detail and no
    // link (Settings is admin-only). Trial and canceled states change nothing for them, so stay quiet.
    if (!isAdmin) {
        if (!isReadOnly) return null;
        return (
            <div className="flex items-center justify-center gap-2 bg-amber-50 px-4 py-2 text-sm text-amber-900 border-b border-amber-200">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Account is read-only — you can view everything, but not make changes. Contact your administrator for more information.</span>
            </div>
        );
    }

    // Native hides every purchase action (IAP rules), so the shortcut is web-only.
    const billingLink = (label: string) => !Capacitor.isNativePlatform() ? (
        <>
            {' '}
            <button
                type="button"
                onClick={() => navigateApp('/settings?tab=billing')}
                className="underline font-medium whitespace-nowrap hover:opacity-80"
            >
                {label}
            </button>
        </>
    ) : null;

    if (isReadOnly) {
        // ReadOnly (7-day grace) has three causes worth distinguishing: a trial that ended, a payment
        // that failed on an otherwise-active subscription (status still Active), or a subscription that
        // genuinely ended. On a payment failure only an admin can fix billing (Settings is admin-only),
        // so a technician is told to ask an administrator rather than pointed at a page they can't open.
        const message =
            subscription.status === 'Trialing'
                ? 'Your trial has ended.'
                : subscription.status === 'Active'
                    // Native hides the Billing tab (IAP rules); recovery is on the web, same as the lock screen.
                    ? Capacitor.isNativePlatform()
                        ? "We couldn't process your last payment. Open the web app and go to Settings → Billing to restore full access."
                        : "We couldn't process your last payment. Update your billing details to restore full access."
                    : 'Your subscription has lapsed.';
        return (
            <div className="flex items-center justify-center gap-2 bg-amber-50 px-4 py-2 text-sm text-amber-900 border-b border-amber-200">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                    {message}
                    {' '}Your account is read-only — you can still view everything, but not make changes.
                    {billingLink('Go to Billing')}
                </span>
            </div>
        );
    }

    // Canceled but still within the paid period: full access continues, but say so and when it ends.
    if (subscription.status === 'Canceled') {
        const ends = subscription.current_period_ends_at ? fmtDate(subscription.current_period_ends_at) : null;
        return (
            <div className="flex items-center justify-center gap-2 bg-amber-50 px-4 py-2 text-sm text-amber-900 border-b border-amber-200">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                    Your subscription is canceled, but you still have full access.{' '}
                    {ends ? `It ends on ${ends}.` : 'It ends soon.'}
                    {billingLink('Go to Billing')}
                </span>
            </div>
        );
    }

    if (subscription.status === 'Trialing') {
        const ends = subscription.trial_ends_at ? fmtDate(subscription.trial_ends_at) : null;
        return (
            <div className="flex items-center justify-center gap-2 bg-blue-50 px-4 py-2 text-sm text-blue-900 border-b border-blue-200">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                    You're on a free trial.{' '}
                    {ends ? `It ends on ${ends}.` : 'It will end soon.'}
                    {billingLink('Upgrade now')}
                </span>
            </div>
        );
    }

    return null;
}

export default SubscriptionBanner;
